import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../environment';
import { RootState } from '../store';
import { PropertyEnquiry, Purchase, User } from '../interfaces';
import { PurchaseUserDetails } from '../interfaces/purchase-user-details';

export const PurchaseAPI = createApi({
  reducerPath: 'purchaseAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: env.apiUrl + '/purchase',
    prepareHeaders: (headers, { getState }) => {
      const { accessToken } = (getState() as RootState).auth;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return headers;
    }
  }),
  tagTypes: ['Purchase'],
  endpoints: (build) => ({
    fetchPurchases: build.query<Purchase[], void>({
      query: () => '/',
      providesTags: result => result
        ? [...result.map(({ id }) => ({ type: 'Purchase' as const, id }))]
        : ['Purchase'],
    }),
    fetchPurchase: build.query<Purchase, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) =>
        result ? [{ type: 'Purchase' as const, id }] : ['Purchase'],
    }),
    createPurchase: build.mutation<Purchase, { propertyID: string, body: PropertyEnquiry }>({
      query: ({ propertyID, body }) => ({
        url: `/${propertyID}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Purchase'],
    }),
    updatePurchase: build
      .mutation<Purchase, { id: string; body: Partial<Purchase> }>({
        query: ({ id, body }) => ({
          url: `/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, { id }) =>
          result ? [{ type: 'Purchase' as const, id }] : ['Purchase'],
      }),
    deletePurchase: build.mutation<Purchase, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) =>
        result ? [{ type: 'Purchase' as const, id }] : ['Purchase'],
    }),
    createPurchaseUserDetails: build.mutation<Purchase, { purchaseID: string; body: FormData }>({
      query: ({ purchaseID, body }) => ({
        url: `/details/${purchaseID}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Purchase'],
    }),
    updatePurchaseUserDetails: build
      .mutation<PurchaseUserDetails, { purchaseID: string; body: FormData }>({
        query: ({ purchaseID, body }) => ({
          url: `/details/${purchaseID}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, { purchaseID }) =>
          result ? [{ type: 'Purchase' as const, id: purchaseID }] : ['Purchase'],
      }),
  }),
});

export const {
  useFetchPurchasesQuery,
  useFetchPurchaseQuery,
  useCreatePurchaseMutation,
  useUpdatePurchaseMutation,
  useDeletePurchaseMutation,
  useCreatePurchaseUserDetailsMutation,
  useUpdatePurchaseUserDetailsMutation,
} = PurchaseAPI;